/**
 * Cart Tier Offer Manager Component for Focal Theme
 * Manages multi-tier cart offers - adds/removes free products based on cart total
 * Version: 2.0.0 - Optimized for Focal Theme
 * 
 * Usage:
 * <cart-tier-offer-manager>
 *   <script type="application/json">
 *     // Tier configuration from metaobjects
 *   </script>
 * </cart-tier-offer-manager>
 */

// GLOBAL state shared across all instances (survives component reconnection)
window.CartTierOfferGlobal = window.CartTierOfferGlobal || {
  isProcessing: false,
  processingBatchId: 0,
  pendingFreebies: new Map(),
  currentBatchAttempts: new Set(),
  lastProcessTime: 0,
  loadingVisible: false,
  progressBarReady: false,
  waitingForProgressBar: false
};

class CartTierOfferManager extends HTMLElement {
  constructor() {
    super();
    
    // Use global state instead of instance state
    this.THROTTLE_DELAY = 500; // Process at most once per 500ms
    
    // Tier state (instance-specific)
    this.tierConfig = null;
    this.currentUnlockedTiers = new Set();
    
    // Property identifier for tier freebies
    this.TIER_PROPERTY_KEY = '_MultipleOfferTier';
    
    // Bind methods
    this.throttledHandler = this.createThrottledHandler();
    this.initializeWithCurrentCart = this.initializeWithCurrentCart.bind(this);
  }

  connectedCallback() {
    console.log('[TierOfferManager] 🎯 Component connected (Focal Theme)');
    
    // Ensure loading indicator exists (only create once globally)
    this.ensureLoadingIndicator();
    
    // Parse tier configuration
    this.parseTierConfig();
    
    if (!this.tierConfig || !this.tierConfig.offers || this.tierConfig.offers.length === 0) {
      console.warn('[TierOfferManager] ⚠️ No tier offers configured');
      return;
    }

    // Log tier configuration to check for duplicates
    console.log('[TierOfferManager] 📋 Tier configuration:', {
      count: this.tierConfig.offers.length,
      tiers: this.tierConfig.offers.map((t, i) => ({
        index: i,
        name: t.propery_name || t.property_name,
        limit: t.tier_limit,
        freebie: t.freebie_product
      }))
    });

    // Listen to Focal theme's cart:updated event
    document.documentElement.addEventListener('cart:updated', this.throttledHandler);
    
    // Listen for progress bar render completion to hide loading
    this.renderCompleteHandler = this.handleRenderComplete.bind(this);
    document.addEventListener('tier:render:complete', this.renderCompleteHandler);
    
    console.log('[TierOfferManager] ✓ Event listeners attached');
    
    // Initial check with proper DOM/cart readiness check
    this.safeInitialize();
  }

  disconnectedCallback() {
    // Cleanup
    document.documentElement.removeEventListener('cart:updated', this.throttledHandler);
    document.removeEventListener('tier:render:complete', this.renderCompleteHandler);
    
    console.log('[TierOfferManager] Component disconnected');
  }

  /**
   * Parse tier configuration from JSON script tag
   */
  parseTierConfig() {
    const configScript = this.querySelector('script[type="application/json"]');
    
    if (!configScript) {
      console.error('[TierOfferManager] ❌ No config script found');
      return;
    }

    try {
      this.tierConfig = JSON.parse(configScript.textContent);
      
      // Sort tiers by limit (ascending)
      if (this.tierConfig.offers) {
        this.tierConfig.offers.sort((a, b) => a.tier_limit - b.tier_limit);
      }
      
      console.log('[TierOfferManager] ✅ Config loaded:', {
        tierCount: this.tierConfig.offers.length,
        tiers: this.tierConfig.offers.map(t => `${t.property_name}: $${t.tier_limit}`)
      });
    } catch (error) {
      console.error('[TierOfferManager] ❌ Error parsing config:', error);
      this.tierConfig = null;
    }
  }

  /**
   * Create throttled event handler
   */
  createThrottledHandler() {
    let queuedUpdate = null;
    
    return (event) => {
      const global = window.CartTierOfferGlobal;
      const now = Date.now();
      const timeSinceLastProcess = now - global.lastProcessTime;
      
      // If called too soon, queue for later
      if (timeSinceLastProcess < this.THROTTLE_DELAY) {
        if (!queuedUpdate) {
          const delay = this.THROTTLE_DELAY - timeSinceLastProcess;
          queuedUpdate = setTimeout(() => {
            queuedUpdate = null;
            this.handleCartUpdate(event);
          }, delay);
        }
        return;
      }
      
      // If already processing, queue for later (GLOBAL CHECK)
      if (global.isProcessing) {
        if (!queuedUpdate) {
          queuedUpdate = setTimeout(() => {
            queuedUpdate = null;
            this.handleCartUpdate(event);
          }, this.THROTTLE_DELAY);
        }
        return;
      }
      
      // Process immediately
      clearTimeout(queuedUpdate);
      queuedUpdate = null;
      global.lastProcessTime = now;
      this.handleCartUpdate(event);
    };
  }

  /**
   * Safe initialization with DOM and cart readiness checks
   */
  safeInitialize() {
    // Wait for DOM to be fully ready
    if (document.readyState === 'loading') {
      console.log('[TierOfferManager] ⏳ Waiting for DOM to be ready...');
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeWithRetry();
      });
    } else {
      // DOM already ready, initialize with retry logic
      this.initializeWithRetry();
    }
  }

  /**
   * Initialize with retry logic for first page load
   */
  async initializeWithRetry(attempt = 1, maxAttempts = 3) {
    const delay = attempt === 1 ? 800 : 1500; // 800ms first try, 1.5s for retries
    
    console.log(`[TierOfferManager] 🔄 Initialize attempt ${attempt}/${maxAttempts} (delay: ${delay}ms)`);
    
    await new Promise(resolve => setTimeout(resolve, delay));
    
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Verify cart is valid
      if (!cart || typeof cart.items === 'undefined') {
        throw new Error('Invalid cart data');
      }
      
      console.log('[TierOfferManager] ✅ Cart data loaded successfully');
      await this.processTiers(cart);
      
    } catch (error) {
      console.error(`[TierOfferManager] ❌ Error on attempt ${attempt}:`, error);
      
      // Retry if we haven't exceeded max attempts
      if (attempt < maxAttempts) {
        console.log(`[TierOfferManager] 🔄 Retrying... (${attempt + 1}/${maxAttempts})`);
        await this.initializeWithRetry(attempt + 1, maxAttempts);
      } else {
        console.error('[TierOfferManager] ❌ Failed to initialize after all attempts');
        this.hideLoading();
      }
    }
  }

  /**
   * Initialize with current cart state (legacy - kept for compatibility)
   */
  async initializeWithCurrentCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (cart) {
        await this.processTiers(cart);
      }
    } catch (error) {
      console.error('[TierOfferManager] ❌ Error initializing:', error);
    }
  }

  /**
   * Handle cart update events
   */
  async handleCartUpdate(event) {
    const timestamp = new Date().toISOString();
    const global = window.CartTierOfferGlobal;
    console.log(`[TierOfferManager] 🔔 [${timestamp}] Cart update event:`, event.type, 
                `isProcessing (GLOBAL): ${global.isProcessing}`);
    
    // Show loading IMMEDIATELY before any processing
    this.showLoading();
    
    let cart = null;
    
    // Try to get cart from event
    if (event.detail && event.detail.cart) {
      cart = event.detail.cart;
    } else {
      // Fetch cart data
      try {
        const response = await fetch('/cart.js');
        cart = await response.json();
      } catch (error) {
        console.error('[TierOfferManager] ❌ Error fetching cart:', error);
        this.hideLoading(); // Hide loading on error
        return;
      }
    }
    
    if (!cart) {
      console.warn('[TierOfferManager] ⚠️ Could not get cart data');
      this.hideLoading(); // Hide loading if no cart
      return;
    }
    
    await this.processTiers(cart);
  }

  /**
   * Main tier processing logic
   */
  async processTiers(cart) {
    const global = window.CartTierOfferGlobal;
    
    if (global.isProcessing) {
      console.log('[TierOfferManager] ⏭️ Already processing (GLOBAL LOCK), skipping...');
      this.hideLoading(); // Hide loading if already processing
      return;
    }

    global.isProcessing = true;
    global.processingBatchId++;
    global.currentBatchAttempts.clear(); // Clear attempts from previous batch
    
    // Loading already shown in handleCartUpdate
    
    console.log(`[TierOfferManager] 🔄 Processing tiers (batch #${global.processingBatchId}) [GLOBAL LOCK ACQUIRED]...`);

    try {
      // Fetch fresh cart data to ensure we have the latest state
      const freshCart = await fetch('/cart.js').then(r => r.json());
      
      // Calculate cart total (excluding tier freebies)
      const cartTotal = this.calculateCartTotal(freshCart);
      
      console.log('[TierOfferManager] 💰 Cart total:', {
        totalCents: cartTotal,
        totalDollars: (cartTotal / 100).toFixed(2),
        originalTotal: (freshCart.original_total_price / 100).toFixed(2)
      });

      // Determine which tiers are unlocked
      const unlockedTiers = this.getUnlockedTiers(cartTotal);
      
      console.log('[TierOfferManager] 🔓 Unlocked tiers:', unlockedTiers.map(t => t.propery_name || t.property_name));
      
      // Check current freebies in cart
      const currentFreebies = freshCart.items.filter(item => 
        item.properties && item.properties[this.TIER_PROPERTY_KEY]
      );
      console.log('[TierOfferManager] 🎁 Current freebies in cart:', currentFreebies.map(item => ({
        title: item.title,
        tier: item.properties[this.TIER_PROPERTY_KEY],
        quantity: item.quantity,
        variantId: item.variant_id
      })));
      
      // Clear stale pending states (older than 5 seconds)
      const now = Date.now();
      for (const [key, timestamp] of global.pendingFreebies.entries()) {
        if (now - timestamp > 5000) {
          console.log(`[TierOfferManager] 🧹 Clearing stale pending: ${key}`);
          global.pendingFreebies.delete(key);
        }
      }
      
      // Track if any changes were made
      let cartWasModified = false;
      
      // Process each tier
      for (let i = 0; i < this.tierConfig.offers.length; i++) {
        const tier = this.tierConfig.offers[i];
        const isUnlocked = unlockedTiers.includes(tier);
        const tierName = tier.propery_name || tier.property_name;
        console.log(`[TierOfferManager] [${new Date().toISOString()}] Processing tier ${i + 1}/${this.tierConfig.offers.length}: ${tierName}, unlocked: ${isUnlocked}`);
        
        const modified = await this.processTier(tier, isUnlocked, freshCart);
        if (modified) {
          cartWasModified = true;
          console.log(`[TierOfferManager] [${new Date().toISOString()}] Tier ${tierName} modified cart`);
        } else {
          console.log(`[TierOfferManager] [${new Date().toISOString()}] Tier ${tierName} no changes needed`);
        }
      }

      // Store current state
      this.currentUnlockedTiers = new Set(unlockedTiers.map(t => t.propery_name || t.property_name));

      // If cart was modified, refresh the cart UI FIRST
      if (cartWasModified) {
        console.log('[TierOfferManager] 🔄 Cart modified, refreshing UI...');
        
        // Mark that we're waiting for progress bar to be ready
        global.progressBarReady = false;
        global.waitingForProgressBar = true;
        
        await this.refreshCartUI();
        
        // Wait for progress bar component to be recreated and ready
        await this.waitForProgressBar();
      }

      // Now dispatch event for progress bar to update (after cart refresh)
      // This ensures the NEW progress bar instance gets the update
      this.dispatchTierUpdateEvent(cartTotal, unlockedTiers);

      // Fallback: Hide loading after 3 seconds if progress bar doesn't respond
      setTimeout(() => {
        if (global.loadingVisible) {
          console.log('[TierOfferManager] ⚠️ Fallback: Hiding loading (timeout)');
          this.hideLoading();
        }
      }, 3000);

    } catch (error) {
      console.error('[TierOfferManager] ❌ Error processing tiers:', error);
      // Hide loading on error
      this.hideLoading();
    } finally {
      global.isProcessing = false;
      console.log(`[TierOfferManager] ✅ Processing complete (batch #${global.processingBatchId}) [GLOBAL LOCK RELEASED]`);
      // Note: Loading is hidden by tier:render:complete event listener
    }
  }

  /**
   * Calculate cart total excluding tier freebies
   */
  calculateCartTotal(cart) {
    if (!cart || !cart.items) return 0;
    
    return cart.items
      .filter(item => {
        // Exclude tier offer freebies
        if (item.properties && item.properties[this.TIER_PROPERTY_KEY]) {
          console.log('[TierOfferManager] 💰 Excluding tier freebie from total:', item.title);
          return false;
        }
        
        return true;
      })
      .reduce((total, item) => total + (item.original_line_price || 0), 0);
  }

  /**
   * Get list of unlocked tiers based on cart total
   */
  getUnlockedTiers(cartTotalCents) {
    const cartTotalDollars = cartTotalCents / 100;
    
    return this.tierConfig.offers.filter(tier => {
      return cartTotalDollars >= tier.tier_limit;
    });
  }

  /**
   * Process a single tier (add/remove freebies)
   * @returns {boolean} True if cart was modified
   */
  async processTier(tier, isUnlocked, cart) {
    const tierProperty = tier.propery_name || tier.property_name; // Handle typo in original code
    
    if (!tierProperty) {
      console.warn('[TierOfferManager] ⚠️ Tier missing property_name:', tier);
      return false;
    }

    // Check if tier has a freebie product
    if (!tier.freebie_product) {
      console.log('[TierOfferManager] ℹ️ Tier has no freebie product:', tierProperty);
      return false;
    }

    // Find existing tier freebies in cart (more thorough check)
    const tierFreebies = cart.items.filter(item => 
      item.properties && 
      item.properties[this.TIER_PROPERTY_KEY] === tierProperty
    );
    
    // Also check if this exact variant is already in cart for this tier
    const hasExactFreebie = cart.items.some(item => 
      (item.variant_id === tier.freebie_product || item.id === tier.freebie_product) &&
      item.properties && 
      item.properties[this.TIER_PROPERTY_KEY] === tierProperty
    );

    let modified = false;

    if (isUnlocked) {
      // Tier is unlocked - ensure freebie is in cart (but only once!)
      const pendingKey = `${tier.freebie_product}:${tierProperty}`;
      const isPending = window.CartTierOfferGlobal.pendingFreebies.has(pendingKey);
      
      // If we found the freebie in cart, clear pending state
      if (hasExactFreebie && isPending) {
        console.log(`[TierOfferManager] ✅ Confirmed freebie in cart, clearing pending: ${tierProperty}`);
        window.CartTierOfferGlobal.pendingFreebies.delete(pendingKey);
      }
      
      // Check if we need to add the freebie
      if (!hasExactFreebie && !isPending && tierFreebies.length === 0) {
        console.log(`[TierOfferManager] [${new Date().toISOString()}] 🎁 Calling addTierFreebie for tier: ${tierProperty}`);
        await this.addTierFreebie(tier);
        console.log(`[TierOfferManager] [${new Date().toISOString()}] ✅ addTierFreebie completed for: ${tierProperty}`);
        modified = true;
      } else if (tierFreebies.length > 1) {
        // Remove duplicates if we somehow have them
        console.warn(`[TierOfferManager] ⚠️ Found ${tierFreebies.length} duplicates for ${tierProperty}, removing extras`);
        for (let i = 1; i < tierFreebies.length; i++) {
          await this.removeTierFreebies([tierFreebies[i]]);
        }
        modified = true;
      } else if (hasExactFreebie) {
        console.log(`[TierOfferManager] ✓ Freebie already in cart: ${tierProperty}`);
      } else if (isPending) {
        console.log(`[TierOfferManager] ⏳ Freebie pending: ${tierProperty}`);
      }
    } else {
      // Tier is locked - remove ALL freebies for this tier if auto-remove is enabled
      const shouldAutoRemove = tier.dont_auto_remove !== 'true' && tier.dont_auto_remove !== true;
      
      if (tierFreebies.length > 0 && shouldAutoRemove) {
        console.log(`[TierOfferManager] 🗑️ Removing ${tierFreebies.length} freebie(s) for locked tier: ${tierProperty}`);
        await this.removeTierFreebies(tierFreebies);
        modified = true;
      }
      
      // Clear pending state for this tier
      const pendingKey = `${tier.freebie_product}:${tierProperty}`;
      window.CartTierOfferGlobal.pendingFreebies.delete(pendingKey);
    }

    return modified;
  }

  /**
   * Add tier freebie to cart
   */
  async addTierFreebie(tier) {
    const global = window.CartTierOfferGlobal;
    const tierProperty = tier.propery_name || tier.property_name;
    const pendingKey = `${tier.freebie_product}:${tierProperty}`;
    const timestamp = new Date().toISOString();
    
    console.log(`[TierOfferManager] [${timestamp}] 🚀 addTierFreebie CALLED for: ${tierProperty}, freebie: ${tier.freebie_product}`);
    
    // Check if we already attempted this in the current batch (GLOBAL)
    if (global.currentBatchAttempts.has(pendingKey)) {
      console.log(`[TierOfferManager] [${timestamp}] ⏭️ Already attempted in GLOBAL batch: ${tierProperty}`);
      return;
    }
    
    // CRITICAL: Set pending state IMMEDIATELY to prevent race conditions (GLOBAL)
    // Check and set in one atomic operation
    if (global.pendingFreebies.has(pendingKey)) {
      const pendingTime = Date.now() - global.pendingFreebies.get(pendingKey);
      console.log(`[TierOfferManager] [${timestamp}] ⏭️ Freebie already pending (GLOBAL): ${tierProperty} (${pendingTime}ms ago)`);
      return;
    }
    
    // Mark as attempted in this batch AND pending RIGHT NOW before any async operations (GLOBAL)
    global.currentBatchAttempts.add(pendingKey);
    global.pendingFreebies.set(pendingKey, Date.now());
    console.log(`[TierOfferManager] [${timestamp}] 📝 Marked as pending GLOBALLY (batch #${global.processingBatchId}): ${pendingKey}`);
    
    try {
      // Fetch fresh cart to check the item isn't already there
      const freshCart = await fetch('/cart.js').then(r => r.json());
      const alreadyInCart = freshCart.items.some(item => 
        (item.variant_id === tier.freebie_product || item.id === tier.freebie_product) &&
        item.properties && 
        item.properties[this.TIER_PROPERTY_KEY] === tierProperty
      );
      
      if (alreadyInCart) {
        console.log(`[TierOfferManager] ⏭️ Freebie already in cart: ${tierProperty}`);
        // Clear pending since it's already there
        window.CartTierOfferGlobal.pendingFreebies.delete(pendingKey);
        return;
      }
      
      // Actually add the freebie
      const addTimestamp = new Date().toISOString();
      console.log(`[TierOfferManager] [${addTimestamp}] 📡 CALLING /cart/add.js for ${tierProperty}`);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: tier.freebie_product,
            quantity: 1,
            properties: { [this.TIER_PROPERTY_KEY]: tierProperty }
          }]
        })
      });
      
      const responseTimestamp = new Date().toISOString();
      
      if (response.ok) {
        const result = await response.json();
        console.log(`[TierOfferManager] [${responseTimestamp}] ✅ Successfully added freebie: ${tier.freebie_product} for ${tierProperty}`);
        // Don't clear pending here - let it be cleared when we confirm it's in cart on next update
      } else {
        const errorText = await response.text();
        console.error(`[TierOfferManager] ❌ Failed to add freebie:`, errorText);
        // Clear pending on failure
        window.CartTierOfferGlobal.pendingFreebies.delete(pendingKey);
      }
      
    } catch (error) {
      console.error(`[TierOfferManager] ❌ Error adding freebie:`, error);
      // Clear pending on error
      window.CartTierOfferGlobal.pendingFreebies.delete(pendingKey);
    }
  }

  /**
   * Remove tier freebies from cart
   */
  async removeTierFreebies(freebies) {
    for (const item of freebies) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: item.key,
            quantity: 0
          })
        });
        
        if (response.ok) {
          console.log(`[TierOfferManager] ✅ Removed freebie: ${item.title}`);
        } else {
          console.error(`[TierOfferManager] ❌ Failed to remove freebie:`, await response.text());
        }
        
      } catch (error) {
        console.error(`[TierOfferManager] ❌ Error removing freebie:`, error);
      }
    }
  }

  /**
   * Refresh cart UI - triggers Focal theme to reload cart
   */
  async refreshCartUI() {
    console.log('[TierOfferManager] 🔄 Dispatching cart:refresh event');
    
    // Dispatch Focal theme's cart refresh event
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: {
        openMiniCart: false,
        openDrawer: false
      }
    }));
  }

  /**
   * Dispatch tier update event for progress bar
   */
  dispatchTierUpdateEvent(cartTotal, unlockedTiers) {
    const event = new CustomEvent('tier:updated', {
      bubbles: true,
      detail: {
        cartTotal,
        cartTotalDollars: cartTotal / 100,
        unlockedTiers: unlockedTiers.map(t => t.propery_name || t.property_name),
        allTiers: this.tierConfig.offers
      }
    });
    
    document.dispatchEvent(event);
    console.log('[TierOfferManager] 📤 Dispatched tier:updated event');
  }

  /**
   * Wait for progress bar component to be ready after cart refresh
   */
  async waitForProgressBar() {
    const global = window.CartTierOfferGlobal;
    const maxWait = 2000; // Maximum 2 seconds
    const startTime = Date.now();
    
    console.log('[TierOfferManager] ⏳ Waiting for progress bar to be ready...');
    
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        
        if (global.progressBarReady) {
          clearInterval(checkInterval);
          global.waitingForProgressBar = false;
          console.log('[TierOfferManager] ✅ Progress bar is ready');
          resolve();
        } else if (elapsed >= maxWait) {
          clearInterval(checkInterval);
          global.waitingForProgressBar = false;
          console.warn('[TierOfferManager] ⚠️ Progress bar wait timeout');
          resolve();
        }
      }, 50); // Check every 50ms
    });
  }

  /**
   * Handle progress bar render completion
   */
  handleRenderComplete(event) {
    console.log('[TierOfferManager] 🎨 Progress bar render complete, hiding loading');
    this.hideLoading();
  }

  /**
   * Ensure loading indicator exists in DOM (create once, persists across cart refreshes)
   */
  ensureLoadingIndicator() {
    const cartDrawer = document.getElementById('mini-cart');
    const existingLoader = document.getElementById('gwp-loading');

    if (existingLoader) {
      if (cartDrawer && existingLoader.parentElement !== cartDrawer) {
        cartDrawer.appendChild(existingLoader);
      }
      return;
    }

    // Create loading indicator HTML
    const loadingDiv = document.createElement('div');
    loadingDiv.id = 'gwp-loading';
    loadingDiv.style.display = 'none';
    loadingDiv.innerHTML = `
      <div class="gwp-loader">
        <div class="gwp-loader-content">
          <svg class="spinner" width="40" height="40" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <circle class="spinner-circle" cx="12" cy="12" r="10" fill="none" stroke-width="2"></circle>
          </svg>
          <p class="gwp-loader-text">Updating cart...</p>
        </div>
      </div>
    `;

    // Inject styles
    if (!document.getElementById('gwp-loading-style')) {
      const style = document.createElement('style');
      style.id = 'gwp-loading-style';
      style.textContent = `
        #gwp-loading {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 30;
          backdrop-filter: blur(2px);
          animation: fadeIn 0.2s ease-in;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .gwp-loader {
          background: white;
          padding: 30px 40px;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
          animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        @keyframes scaleIn {
          from {
            transform: scale(0.8);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        .gwp-loader-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 15px;
        }

        .gwp-loader-text {
          margin: 0;
          font-size: 14px;
          font-weight: 500;
          color: #333;
          text-align: center;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        .spinner-circle {
          stroke: #22c55e;
          stroke-linecap: round;
          stroke-dasharray: 50, 200;
          stroke-dashoffset: 0;
          animation: spinner-dash 1.5s ease-in-out infinite;
        }

        @keyframes spin {
          100% { transform: rotate(360deg); }
        }

        @keyframes spinner-dash {
          0% {
            stroke-dasharray: 1, 200;
            stroke-dashoffset: 0;
          }
          50% {
            stroke-dasharray: 100, 200;
            stroke-dashoffset: -50;
          }
          100% {
            stroke-dasharray: 100, 200;
            stroke-dashoffset: -150;
          }
        }
      `;
      document.head.appendChild(style);
    }

    if (cartDrawer) {
      cartDrawer.appendChild(loadingDiv);
    } else {
      document.body.appendChild(loadingDiv);
    }
    
    console.log('[TierOfferManager] 💾 Loading indicator created');
  }

  /**
   * Check if the cart drawer is currently open
   */
  isCartDrawerOpen() {
    const cartDrawer = document.getElementById('mini-cart');
    return Boolean(cartDrawer && cartDrawer.hasAttribute('open'));
  }

  /**
   * Show loading indicator
   */
  showLoading() {
    if (!this.isCartDrawerOpen()) {
      return;
    }

    // Use global flag to prevent multiple show calls
    if (window.CartTierOfferGlobal.loadingVisible) {
      return;
    }

    this.ensureLoadingIndicator();
    
    const loader = document.getElementById('gwp-loading');
    if (loader) {
      loader.style.display = 'flex';
      window.CartTierOfferGlobal.loadingVisible = true;
      console.log('[TierOfferManager] ⏳ Loading indicator shown');
    }
  }

  /**
   * Hide loading indicator
   */
  hideLoading() {
    const loader = document.getElementById('gwp-loading');
    if (loader && window.CartTierOfferGlobal.loadingVisible) {
      // Small delay to ensure smooth transition
      setTimeout(() => {
        loader.style.display = 'none';
        window.CartTierOfferGlobal.loadingVisible = false;
        console.log('[TierOfferManager] ✅ Loading indicator hidden');
      }, 300);
    } else {
      window.CartTierOfferGlobal.loadingVisible = false;
    }
  }
}

// Register custom element
if (!customElements.get('cart-tier-offer-manager')) {
  customElements.define('cart-tier-offer-manager', CartTierOfferManager);
  console.log('[TierOfferManager] 🎨 Component registered ✓');
}
